import React from "react";
import { useTranslation } from "react-i18next";
import RatingStars from "../../ui/RatingStars";
import { motion, useReducedMotion } from "framer-motion";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import {
  FiArrowRight,
  FiClock,
  FiSun,
  FiMail,
  FiMapPin,
} from "react-icons/fi";
import { Button } from "../../ui/Button";
import * as D from "./Farms.desktop.styled";

const DeliveryIcon = () => <FiClock aria-hidden="true" />;
const LocationIcon = () => <FiMapPin aria-hidden="true" />;

const OwnerBadge = ({ name, imageUrl }) => {
  if (!name) return null;
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const initials = parts
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <D.OwnerBadge>
      {imageUrl ? (
        <D.OwnerAvatar src={imageUrl} alt={name} loading="lazy" />
      ) : (
        <D.OwnerInitials aria-hidden="true">{initials}</D.OwnerInitials>
      )}
      <D.OwnerName>{name}</D.OwnerName>
    </D.OwnerBadge>
  );
};

const FarmCardDesktop = ({
  farm,
  variant = "grid",
  index = 0,
  isSaved,
  onToggleWishlist,
  onOpenModal,
  onViewFarm,
  onContact,
  deliveryLabel,
  ctaLabels,
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const handleCardKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpenModal(farm);
    }
  };

  return (
    <D.Card
      as={motion.article}
      $variant={variant}
      initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduceMotion ? 0 : 0.45,
        delay: reduceMotion ? 0 : Math.min(index * 0.05, 0.3),
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={() => onOpenModal(farm)}
      role="button"
      tabIndex={0}
      onKeyDown={handleCardKey}
      aria-label={t("farms.card_open", {
        defaultValue: "Open details for {{name}}",
        name: farm.name,
      })}
      data-testid="farm-card"
    >
      <D.CardMedia $variant={variant}>
        <D.CardImage $src={farm.image} aria-hidden="true" />
        <D.CardScrim aria-hidden="true" />

        {farm.isOrganic ? (
          <D.OrganicBadge>
            <FiSun aria-hidden="true" />
            {t("farms.badge.organic", { defaultValue: "Organic" })}
          </D.OrganicBadge>
        ) : null}

        <D.WishlistBtn
          type="button"
          $saved={isSaved}
          onClick={(event) => {
            event.stopPropagation();
            onToggleWishlist(event, farm.id);
          }}
          aria-label={
            isSaved
              ? t("farms.wishlist.remove", {
                  defaultValue: "Remove from wishlist",
                })
              : t("farms.wishlist.add", { defaultValue: "Save to wishlist" })
          }
        >
          {isSaved ? <FaHeart /> : <FaRegHeart />}
        </D.WishlistBtn>

        <OwnerBadge
          name={farm.ownerFirstName || farm.ownerName}
          imageUrl={farm.ownerProfilePictureUrl}
        />
      </D.CardMedia>

      <D.CardBody $variant={variant}>
        {farm.tags && farm.tags.length ? (
          <D.TagRow>
            {farm.tags.slice(0, 3).map((tag) => (
              <D.Tag key={`${farm.id}-${tag}`}>{tag}</D.Tag>
            ))}
          </D.TagRow>
        ) : null}

        <D.CardTitle>{farm.name}</D.CardTitle>

        <D.MetaRow>
          <D.MetaItem>
            <LocationIcon />
            {farm.location}
          </D.MetaItem>
          <D.MetaItem>
            <DeliveryIcon />
            {deliveryLabel}
          </D.MetaItem>
          {/* A farm with no reviews yet says so, rather than showing an empty five stars that
              reads as a zero score. */}
          <D.MetaItem>
            {farm.reviewCount > 0 ? (
              <RatingStars rating={farm.averageRating} count={farm.reviewCount} size="0.85rem" />
            ) : (
              <D.MetaMuted>
                {t("farms.no_reviews", { defaultValue: "No reviews yet" })}
              </D.MetaMuted>
            )}
          </D.MetaItem>
        </D.MetaRow>

        <D.CardDescription>{farm.description}</D.CardDescription>

        <D.CardActions>
          <Button
            type="button"
            variant="primary"
            iconRight={<FiArrowRight aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              onViewFarm(farm.id);
            }}
          >
            {ctaLabels[farm.primaryAction] ?? ctaLabels.viewFarm}
          </Button>
          {farm.secondaryAction ? (
            <Button
              type="button"
              variant="outline"
              iconLeft={<FiMail aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                onContact(farm.id);
              }}
            >
              {ctaLabels[farm.secondaryAction] ?? ctaLabels.contact}
            </Button>
          ) : null}
        </D.CardActions>
      </D.CardBody>

      <D.CardAccent aria-hidden="true" />
    </D.Card>
  );
};

export default FarmCardDesktop;
